import { describeUseCase, UseCase } from "./useCases";

/**
 * The use case a booking flow's Redux `bookingCreation.useCase` stands for.
 *
 * That value is not always a use case: the apartment flow sets the alias
 * `"room"` (`RoomRentalDialog` → `useBookingFlowLifecycle`), the venue flow
 * sets `"venue"`, and the other flows set their own names or non-use-case
 * values (`"voucher"`, `"temp_storage_new_booking"`). `describeUseCase("room")`
 * is `null`, so a site that asks the use-case description about the flow
 * resolves the value here first — the one place the alias is translated —
 * and then asks a property of the result.
 *
 * `"apartment"` is NOT a flow value: no flow sets it (the apartment flow is
 * `"room"`), and the sites that read the Redux value have always treated it
 * as "no room flow" (`requiredPersonalDataFields`, the slice's step
 * validators). It stays unknown here so moving them onto this helper changes
 * nothing.
 *
 * The value lives in Redux only (set on dialog mount, cleared on unmount); it
 * is not written to storage or the URL, so nothing persisted depends on the
 * alias. Renaming the stored value to `"apartment"` would be the cleaner end
 * state, but it reaches every reader of the raw value (the station flows'
 * `state.useCase === …` checks in `bookingCreationSlice`, `BookingFlowShell`,
 * `PersonalDataStepShared`), so it is left to its own change — which then
 * also drops the `"apartment"` exclusion below.
 *
 * `null` for anything that is not a live use case's flow.
 */
export const flowUseCase = (raw: string | null | undefined): UseCase | null => {
  const key = String(raw ?? "").trim().toLowerCase();
  if (key === "room") return "apartment";
  if (key === "apartment") return null;
  return describeUseCase(key) ? (key as UseCase) : null;
};
